/**
 * Artifact detail slice — data for the /artifact page.
 *
 * - fetchArtifactDetail: POST /artifacts/detail via beaconApi, mapped to the
 *   ArtifactDetailView view model. When several artifacts match, the one whose
 *   id equals the searched id is shown, else the latest (last by createdAt).
 *
 * Runs against the selected region, or every region in a global search.
 * `data` holds the active region's hit; `hits` carries the rest.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ArtifactDetailView } from './artifactDetailTypes';
import { mapArtifactDetail } from './artifactDetailMapper';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';
import {
  FanOutResult, RegionLookupState, describeFailures, fanOutByRegion,
  regionBases, regionLookupInitialState,
} from '../regionFanOut';

export interface ArtifactDetailState extends RegionLookupState<ArtifactDetailView> {
  data: ArtifactDetailView | null;
  /** Other artifacts returned for the same identifier, in the active region. */
  otherArtifactIds: string[];
  loading: boolean;
  error: string | null;
  notFound: boolean;
  currentRequestId: string | null;
  /** Entity id of the last fetch; re-dispatched on region change. */
  currentId: string | null;
}

export const artifactDetailInitialState: ArtifactDetailState = {
  data: null,
  otherArtifactIds: [],
  loading: false,
  error: null,
  notFound: false,
  currentRequestId: null,
  currentId: null,
  ...regionLookupInitialState,
};

export const fetchArtifactDetail = createAsyncThunk<
  FanOutResult<ArtifactDetailView>, string, { state: RootState; rejectValue: string }
>('artifactDetail/fetch', async (artifactId, { getState }) => {
  const lookup = async (apiBase: string) => {
    const response = await beaconApi.getArtifactDetail([artifactId], apiBase);
    const found = response.artifacts ?? [];
    if (found.length === 0) return { detail: null, otherIds: [] };
    const primary = found.find(artifact => artifact.id === artifactId) ?? found[found.length - 1];
    return {
      detail: mapArtifactDetail(primary),
      otherIds: found.filter(artifact => artifact !== primary).map(artifact => artifact.id),
    };
  };
  return fanOutByRegion(regionBases(getState().env), lookup);
});

/** Points `data` at one region's hit; the sections re-render from it. */
const activate = (state: ArtifactDetailState, region: string | null) => {
  const hit = state.hits.find(h => h.region === region) ?? state.hits[0];
  state.activeRegion = hit?.region ?? null;
  state.data = hit?.detail ?? null;
  state.otherArtifactIds = hit?.otherIds ?? [];
};

const artifactDetailSlice = createSlice({
  name: 'artifactDetail',
  initialState: artifactDetailInitialState,
  reducers: {
    clearArtifactDetail: () => artifactDetailInitialState,
    selectArtifactRegion: (state, action: PayloadAction<string>) => {
      activate(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArtifactDetail.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.notFound = false;
        state.currentRequestId = action.meta.requestId;
        state.currentId = action.meta.arg;
      })
      .addCase(fetchArtifactDetail.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        const { hits, searchedRegions, errors } = action.payload;
        state.loading = false;
        state.hits = hits;
        state.searchedRegions = searchedRegions;
        state.regionErrors = errors;
        activate(state, null);
        // Absence is only proven if every region answered; else it is an error.
        state.notFound = hits.length === 0 && errors.length === 0;
        state.error = hits.length === 0 && errors.length > 0
          ? `No region returned an artifact. ${describeFailures(errors)}`
          : null;
      })
      .addCase(fetchArtifactDetail.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch artifact detail';
      });
  },
});

export const { clearArtifactDetail, selectArtifactRegion } = artifactDetailSlice.actions;
export default artifactDetailSlice.reducer;
